import { Box, Typography, Autocomplete, Grid, Checkbox } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ExcelUploadInput from '../../../../../reusable-components/excel-input/ExcelUploadInput';
import SimpleAutocompleteProps from '../../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';

OrderGuideExcelUpload.propTypes = {
  formData: PropTypes.object,
  setFormData: PropTypes.func
}

export default function OrderGuideExcelUpload({ formData, setFormData })
{
  const [sheets, setSheets] = useState(null);
  const [headerRow, setHeaderRow] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [useVendorSku, setUseVendorSku] = useState(false);

  const isReadyForSubmit = () => formData?.facilityCol && formData.categoryCol && (!useVendorSku && (formData.psProductIdCol || formData.descriptionCol) ||
    useVendorSku && formData.vendorItemIdCol && formData.vendorCol);

  useEffect(() => {
    if (isReadyForSubmit()) {   
      setTableData(Object.values(excelData.map(row => ({
        facility: row[formData.facilityCol],
        category: row[formData.categoryCol],
        psItemId: useVendorSku ? null : row[formData.psProductIdCol],
        description: useVendorSku ? null : row[formData.descriptionCol],
        vendorItemId: !useVendorSku ? null : row[formData.vendorItemIdCol],
        vendor: !useVendorSku ? null : row[formData.vendorCol],
      })).filter(row => row.facility && row.category && ((useVendorSku && row.vendorItemId && row.vendor) || (!useVendorSku && (row.psItemId || row.description))))
        .reduce((accumulator, currentValue) => {
        const key = `${currentValue.facility}-${currentValue.category}`;
        accumulator[key] = accumulator[key] ?? { facility: currentValue.facility, category: currentValue.category };
        accumulator[key].itemCount = (accumulator[key].itemCount ?? 0) + 1;
          accumulator[key].id = key;
        return accumulator;
        }, {}))
        .map(row => ({ ...row })));
    }
  }, [formData, excelData, useVendorSku]);

  const columns = [
    {
      field: "facility",
      headerName: 'Facility',
      minWidth: 100,
      flex: 1
    },
    {
      field: "category",
      headerName: 'Category',
      minWidth: 100,
      flex: 1
    },
    {
      field: "itemCount",
      headerName: 'Item Count',
      minWidth: 100,
      flex: 1
    },
  ];

  return <Grid sx={{ mt: 0 }} container spacing={2}>
    <Grid item xs={12}>
      <Box display="flex" alignItems="center">
        <ExcelUploadInput
          setSheets={setSheets}
          setFile={(file) => setFormData(data => ({ ...(data ?? {}), file }))}
          setSelectedSheet={(sheet) => setFormData(data => ({ ...(data ?? {}), sheet }))}
          setHeaderRow={setHeaderRow}
          setExcelData={setExcelData}
          selectedSheet={formData?.sheet}
          ignoreEmptyRows
        />
        <Checkbox checked={useVendorSku} onChange={(e) => setUseVendorSku(e.target.checked)} />Use vendor/sku
      </Box>
    </Grid>
    <Grid item xs={12}>
      <Typography>Sheet</Typography>
      <Autocomplete
        fullWidth
        value={formData?.sheet ? { value: formData.sheet, label: formData.sheet } : null}
        onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), sheet: newValue?.value }))}
        options={sheets?.map(sheet => ({ value: sheet, label: sheet })) ?? []}
        {...SimpleAutocompleteProps}
      />
    </Grid>
    <Grid container item spacing={2}>
      <Grid item xs={4}>
        <Typography>Facility</Typography>
        <Autocomplete
          fullWidth
          value={formData?.facilityCol ? headerRow.find(cell => cell.value === formData.facilityCol) : null}
          onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), facilityCol: newValue?.value }))}
          options={headerRow}
          {...SimpleAutocompleteProps}
        />
      </Grid>
      <Grid item xs={4}>
        <Typography>Category</Typography>
        <Autocomplete
          fullWidth
          value={formData?.categoryCol ? headerRow.find(cell => cell.value === formData.categoryCol) : null}
          onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), categoryCol: newValue?.value }))}
          options={headerRow}
          {...SimpleAutocompleteProps}
        />
      </Grid>
      {!useVendorSku && <>
        <Grid item xs={2}>
          <Typography>PS Product Id</Typography>
          <Autocomplete
            fullWidth
            value={formData?.psProductIdCol ? headerRow.find(cell => cell.value === formData.psProductIdCol) : null}
            onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), psProductIdCol: newValue?.value }))}
            options={headerRow}
            {...SimpleAutocompleteProps}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography>Description</Typography>
          <Autocomplete
            fullWidth
            value={formData?.descriptionCol ? headerRow.find(cell => cell.value === formData.descriptionCol) : null}
            onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), descriptionCol: newValue?.value }))}
            options={headerRow}
            {...SimpleAutocompleteProps}
          />
        </Grid>
      </>}
      {useVendorSku && <>
        <Grid item xs={2}>
          <Typography>Vendor</Typography>
          <Autocomplete
            fullWidth
            value={formData?.vendorCol ? headerRow.find(cell => cell.value === formData.vendorCol) : null}
            onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), vendorCol: newValue?.value }))}
            options={headerRow}
            {...SimpleAutocompleteProps}
          />
        </Grid>
        <Grid item xs={2}>
          <Typography>Vendor Item Id</Typography>
          <Autocomplete
            fullWidth
            value={formData?.vendorItemIdCol ? headerRow.find(cell => cell.value === formData.vendorItemIdCol) : null}
            onChange={(event, newValue) => setFormData(data => ({ ...(data ?? {}), vendorItemIdCol: newValue?.value }))}
            options={headerRow}
            {...SimpleAutocompleteProps}
          />
        </Grid>
      </>}
    </Grid>
    {isReadyForSubmit() &&
      <Grid item xs={12}>
        <CustomDataGrid
          gridId="admin-manage-order-guide-excel-upload"
          boxSX={{ height: '300px' }}
          scrollbarHeight={null}
          tableBoxSX={{ height: 'calc(100% - 52px)' }}
          data={tableData.length > 0 ? tableData.slice(1) : tableData}
          isLoading={false}
          gridColumns={columns}
          sort={[{ field: 'facility', sort: 'asc' }]}
        />
      </Grid>}
  </Grid>;
}
